import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PageHeader, KpiCard, Panel, EmptyState, formatEur } from "@/components/shared/PageUi";
import CrudList from "@/components/shared/CrudList";
import { toast } from "sonner";
import {
  Users, Wallet, FileText, AlertTriangle, Plus, Pencil, Trash2, Search,
  Download, CheckCircle2, FilePlus2, Receipt, TrendingUp, Briefcase, Clock,
  ListChecks, BarChart3, FileCode2, PlayCircle, KeyRound,
} from "lucide-react";

const EMPLOYMENT_TYPES = [
  { value: "monthly", label: "Kuukausipalkkainen" },
  { value: "hourly", label: "Tuntipalkkainen" },
  { value: "gig", label: "Keikkatyöntekijä" },
  { value: "yel", label: "Yrittäjä (YEL)" },
];

const STATUS_BADGE = {
  draft: { label: "Luonnos", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  paid: { label: "Maksettu", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  sent: { label: "Lähetetty", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  cancelled: { label: "Peruutettu", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
};

const TE_STATUS = [
  { value: "draft", label: "Luonnos" },
  { value: "submitted", label: "Lähetetty" },
  { value: "approved", label: "Hyväksytty" },
  { value: "rejected", label: "Hylätty" },
];

const defaultEmployee = {
  employee_number: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  address: "",
  postal_code: "",
  city: "",
  national_id: "",
  date_of_birth: "",
  iban: "",
  employment_type: "monthly",
  start_date: "",
  end_date: "",
  monthly_salary: 0,
  hourly_rate: 0,
  regular_hours_per_month: 160,
  tax_card: { base_percent: 0, additional_percent: 0, income_threshold: 0, card_type: "main" },
  tyel_contract_number: "",
  tyel_company: "",
  benefits: { phone: 0, lunch: 0, car: 0, accommodation: 0, other: 0 },
  vacation_days_per_year: 25,
  collective_agreement: "",
  union: "",
  union_fee_percent: 0,
  notes: "",
  is_active: true,
};

const defaultVacation = { employee_id: "", vacation_year: new Date().getFullYear(), accrued_days: 0, used_days: 0, holiday_pay_accrued: 0, holiday_pay_paid: 0, notes: "" };
const defaultAbsence = { employee_id: "", absence_type: "sick", start_date: "", end_date: "", days: 0, paid: false, affects_vacation_accrual: true, notes: "" };

const todayStr = () => new Date().toISOString().slice(0, 10);
const firstOfMonthStr = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const lastOfMonthStr = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); };

const defaultRun = () => ({
  name: "",
  pay_period_start: firstOfMonthStr(),
  pay_period_end: lastOfMonthStr(),
  pay_date: todayStr(),
  employee_ids: [],
});

export default function HRDashboard() {
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [empDialog, setEmpDialog] = useState({ open: false, mode: "create", data: defaultEmployee });
  const [portalDialog, setPortalDialog] = useState({ open: false, employee: null, email: "", password: "" });
  const [vacDialog, setVacDialog] = useState({ open: false, mode: "create", data: defaultVacation });
  const [absDialog, setAbsDialog] = useState({ open: false, mode: "create", data: defaultAbsence });
  const [runDialog, setRunDialog] = useState({ open: false, data: defaultRun() });
  const [runDetail, setRunDetail] = useState(null);   // { run, payslips } – avattu lista
  const [runs, setRuns] = useState([]);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportTab, setReportTab] = useState("bookkeeping");
  const [bkReport, setBkReport] = useState(null);
  const [sumReport, setSumReport] = useState(null);
  const [vacReport, setVacReport] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, e, p, v, a, r] = await Promise.all([
        api.get("/payroll/stats"),
        api.get("/payroll/employees"),
        api.get("/payroll/payslips?limit=500"),
        api.get("/payroll/vacations"),
        api.get("/payroll/absences"),
        api.get("/payroll/runs"),
      ]);
      setStats(s.data);
      setEmployees(e.data || []);
      setPayslips(p.data || []);
      setVacations(v.data || []);
      setAbsences(a.data || []);
      setRuns(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // -- Employees CRUD
  const openCreateEmp = () => setEmpDialog({ open: true, mode: "create", data: defaultEmployee });
  const openEditEmp = (emp) => setEmpDialog({
    open: true, mode: "edit",
    data: {
      ...defaultEmployee, ...emp,
      tax_card: { ...defaultEmployee.tax_card, ...(emp.tax_card || {}) },
      benefits: { ...defaultEmployee.benefits, ...(emp.benefits || {}) },
    },
  });
  const saveEmployee = async () => {
    const d = empDialog.data;
    if (!d.first_name?.trim() || !d.last_name?.trim()) return toast.error("Etunimi ja sukunimi pakollisia");
    setSaving(true);
    try {
      if (empDialog.mode === "create") {
        await api.post("/payroll/employees", d);
        toast.success("Työntekijä luotu");
      } else {
        await api.put(`/payroll/employees/${d.id}`, d);
        toast.success("Työntekijä päivitetty");
      }
      setEmpDialog({ open: false, mode: "create", data: defaultEmployee });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  const deleteEmployee = async (emp) => {
    if (!window.confirm(`Poista työntekijä ${emp.first_name} ${emp.last_name}?`)) return;
    try {
      const r = await api.delete(`/payroll/employees/${emp.id}`);
      toast.success(r.data?.message || "Poistettu");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // -- Portal access for employee (self-service-portaali)
  const openPortalAccess = (emp) => setPortalDialog({
    open: true, employee: emp,
    email: emp.portal_email || emp.email || "",
    password: Math.random().toString(36).slice(-10) + "A1!",
  });
  const savePortalAccess = async () => {
    const d = portalDialog;
    if (!d.email?.trim() || !d.password) return toast.error("Sähköposti ja salasana pakollisia");
    setSaving(true);
    try {
      await api.post(`/payroll/employees/${d.employee.id}/portal-access`, {
        portal_email: d.email.trim(),
        portal_password: d.password,
        enabled: true,
      });
      toast.success(`Portaalipääsy asetettu. Lähetä ${d.employee.first_name}lle linkki /employee-portal/login + tilapäinen salasana.`);
      setPortalDialog({ open: false, employee: null, email: "", password: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  const disablePortalAccess = async (emp) => {
    if (!window.confirm(`Poista portaalipääsy työntekijältä ${emp.first_name} ${emp.last_name}?`)) return;
    try {
      await api.post(`/payroll/employees/${emp.id}/portal-access/disable`);
      toast.success("Portaalipääsy poistettu");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };


  // -- Payslip actions
  const markPaid = async (psId) => {
    try {
      await api.post(`/payroll/payslips/${psId}/mark-paid`);
      toast.success("Merkitty maksetuksi (+ kirjattu kirjanpitoon)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deletePayslip = async (ps) => {
    if (!window.confirm(`Poista/peruuta palkkalaskelma ${ps.payslip_number}?`)) return;
    try {
      await api.delete(`/payroll/payslips/${ps.id}`);
      toast.success("Tehty");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadPdf = async (ps) => {
    try {
      const r = await api.get(`/payroll/payslips/${ps.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `palkkalaskelma_${ps.payslip_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 1000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadTulorekisteriXml = async (ps) => {
    try {
      const r = await api.get(`/payroll/tulorekisteri/payslip/${ps.id}/xml`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tulorekisteri_${ps.payslip_number}.xml`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 1000);
      toast.success("Tulorekisteri-XML ladattu");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // -- Payroll Runs (palkkalistat)
  const openRunDialog = () => setRunDialog({ open: true, data: { ...defaultRun(), employee_ids: [] } });
  const submitRun = async () => {
    setSaving(true);
    try {
      const r = await api.post("/payroll/runs", runDialog.data);
      toast.success(`Palkkalista luotu (${r.data?.employee_count || 0} laskelmaa)`);
      setRunDialog({ open: false, data: defaultRun() });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  const openRunDetail = async (run) => {
    try {
      const r = await api.get(`/payroll/runs/${run.id}`);
      setRunDetail(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const markRunPaid = async (run) => {
    if (!window.confirm(`Merkitse palkkalistan "${run.name}" KAIKKI luonnokset maksetuiksi?`)) return;
    try {
      const r = await api.post(`/payroll/runs/${run.id}/mark-paid`);
      toast.success(`Maksettu ${r.data?.marked || 0} laskelmaa + kirjattu kirjanpitoon`);
      setRunDetail(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteRun = async (run) => {
    if (!window.confirm(`Poista palkkalista "${run.name}" ja kaikki sen luonnokset?`)) return;
    try {
      await api.delete(`/payroll/runs/${run.id}`);
      toast.success("Palkkalista poistettu");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // -- Reports
  const loadReports = useCallback(async (year) => {
    try {
      const [bk, sum, vac] = await Promise.all([
        api.get(`/payroll/reports/bookkeeping?year=${year}`),
        api.get(`/payroll/reports/summary?year=${year}`),
        api.get(`/payroll/reports/vacation-summary?year=${year}`),
      ]);
      setBkReport(bk.data);
      setSumReport(sum.data);
      setVacReport(vac.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { loadReports(reportYear); }, [reportYear, loadReports, payslips.length, vacations.length]);

  // -- Vacation / Absence
  const saveVacation = async () => {
    const d = vacDialog.data;
    if (!d.employee_id) return toast.error("Valitse työntekijä");
    setSaving(true);
    try {
      if (vacDialog.mode === "create") await api.post("/payroll/vacations", d);
      else await api.put(`/payroll/vacations/${d.id}`, d);
      toast.success("Tallennettu");
      setVacDialog({ open: false, mode: "create", data: defaultVacation });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  const saveAbsence = async () => {
    const d = absDialog.data;
    if (!d.employee_id || !d.start_date) return toast.error("Työntekijä ja alkupäivä pakollisia");
    setSaving(true);
    try {
      if (absDialog.mode === "create") await api.post("/payroll/absences", d);
      else await api.put(`/payroll/absences/${d.id}`, d);
      toast.success("Tallennettu");
      setAbsDialog({ open: false, mode: "create", data: defaultAbsence });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  const deleteRow = async (endpoint, id, label) => {
    if (!window.confirm(`Poista ${label}?`)) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Poistettu"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  // -- Time entries (käyttää geneeristä CrudListiä, vanha endpoint)
  const timeEntriesConfig = {
    endpoint: "/hr/time-entries",
    testidPrefix: "time-entries",
    emptyMessage: "Ei tuntimerkintöjä",
    defaults: { employee_id: "", employee_name: "", date: new Date().toISOString().slice(0, 10), hours: 0, status: "draft" },
    fields: [
      { key: "employee_name", label: "Työntekijä", type: "text", required: true },
      { key: "date", label: "Päivämäärä", type: "date", required: true },
      { key: "start_time", label: "Alku", type: "text", placeholder: "08:00" },
      { key: "end_time", label: "Loppu", type: "text", placeholder: "16:00" },
      { key: "break_minutes", label: "Tauot (min)", type: "number", step: "1" },
      { key: "hours", label: "Tunnit", type: "number" },
      { key: "project", label: "Projekti", type: "text" },
      { key: "task_description", label: "Tehtävä", type: "text", span: 2 },
      { key: "status", label: "Tila", type: "select", options: TE_STATUS },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "date", label: "Pvm", format: "date" },
      { key: "employee_name", label: "Työntekijä" },
      { key: "project", label: "Projekti" },
      { key: "task_description", label: "Tehtävä" },
      { key: "hours", label: "Tunnit", format: "num", digits: 2, align: "right" },
      { key: "status", label: "Tila", format: "badge" },
    ],
  };

  const filteredEmployees = employees.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [e.first_name, e.last_name, e.email, e.employee_number].some(v => (v || "").toLowerCase().includes(s));
  });
  const filteredPayslips = payslips.filter(p => {
    if (statusFilter && p.status !== statusFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return [p.payslip_number, p.employee_name].some(v => (v || "").toLowerCase().includes(s));
  });
  const empName = (id) => {
    const e = employees.find(x => x.id === id);
    return e ? `${e.first_name} ${e.last_name}` : "—";
  };

  return (
    <div className="space-y-6" data-testid="hr-dashboard">
      <PageHeader
        kicker="Sovellus • palkkaus.fi -tyylinen"
        title="HR & Palkat"
        description="Työntekijät, palkkalaskelmat, lomat ja vuosikertymät. Lakivaatimusten mukaan."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Link to="/hr/payslip/new">
              <Button className="btn-luxe inline-flex items-center gap-2" data-testid="hr-new-payslip">
                <FilePlus2 className="w-4 h-4" /> Uusi palkkalaskelma
              </Button>
            </Link>
            <Button variant="outline" onClick={openCreateEmp} className="inline-flex items-center gap-2 border-amber-400/30" data-testid="hr-new-employee">
              <Plus className="w-4 h-4" /> Uusi työntekijä
            </Button>
            <Link to="/hr/settings">
              <Button variant="outline" className="inline-flex items-center gap-2 border-white/10" data-testid="hr-settings-link">
                Asetukset
              </Button>
            </Link>
          </div>
        }
      />

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={<Users className="w-4 h-4" />} label="Aktiiviset työntekijät" value={stats?.active_employees ?? "—"} testid="kpi-active-employees" loading={loading} />
        <KpiCard icon={<Wallet className="w-4 h-4" />} label={`Kuluva kk (${stats?.current_month || ""})`} value={formatEur(stats?.current_month_gross || 0)} accent="text-emerald-300" testid="kpi-month-gross" loading={loading} />
        <KpiCard icon={<TrendingUp className="w-4 h-4" />} label="Vuosibrutto" value={formatEur(stats?.ytd_gross || 0)} accent="text-blue-300" testid="kpi-ytd-gross" loading={loading} />
        <KpiCard icon={<FileText className="w-4 h-4" />} label="Luonnokset" value={stats?.drafts_count ?? "—"} accent="text-amber-300" testid="kpi-drafts" loading={loading} />
      </div>

      {/* Hae */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Hae nimellä, sähköpostilla, laskelman numerolla…"
            className="pl-9 bg-black/30 border-white/10"
            data-testid="hr-search"
          />
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-white/[0.03] border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Yleiskuva</TabsTrigger>
          <TabsTrigger value="employees" data-testid="tab-employees">Työntekijät</TabsTrigger>
          <TabsTrigger value="payslips" data-testid="tab-payslips">Palkkalaskelmat</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs">Palkkalistat</TabsTrigger>
          <TabsTrigger value="time-entries" data-testid="tab-time">Työajat</TabsTrigger>
          <TabsTrigger value="vacations" data-testid="tab-vacations">Lomat</TabsTrigger>
          <TabsTrigger value="absences" data-testid="tab-absences">Poissaolot</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Raportit</TabsTrigger>
          <TabsTrigger value="archive" data-testid="tab-archive">Arkisto</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff">Henkilökunta</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Aktiiviset työntekijät">
              {filteredEmployees.length === 0 ? (
                <EmptyState icon={<Users className="w-8 h-8" />} message="Ei työntekijöitä – luo ensimmäinen" action={<Button onClick={openCreateEmp} className="btn-luxe">Lisää työntekijä</Button>} />
              ) : (
                <div className="divide-y divide-white/5">
                  {filteredEmployees.slice(0, 6).map(e => (
                    <div key={e.id} className="flex items-center justify-between py-2.5">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-purple-600 text-black flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {(e.first_name?.[0] || "?") + (e.last_name?.[0] || "")}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">{e.first_name} {e.last_name}</p>
                          <p className="text-[11px] text-white/40">{EMPLOYMENT_TYPES.find(t => t.value === e.employment_type)?.label || e.employment_type}</p>
                        </div>
                      </div>
                      <Link to={`/hr/payslip/new?employee=${e.id}`}>
                        <Button size="sm" variant="outline" className="border-amber-400/30 text-amber-200 hover:bg-amber-500/10 inline-flex items-center gap-1">
                          <FilePlus2 className="w-3 h-3" /> Uusi laskelma
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Viimeisimmät palkkalaskelmat">
              {payslips.length === 0 ? (
                <EmptyState icon={<Receipt className="w-8 h-8" />} message="Ei laskelmia vielä" />
              ) : (
                <div className="divide-y divide-white/5">
                  {payslips.slice(0, 6).map(p => {
                    const sb = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
                    return (
                      <div key={p.id} className="flex items-center justify-between py-2.5">
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">{p.employee_name} <span className="text-white/40 text-xs">· {p.payslip_number}</span></p>
                          <p className="text-[11px] text-white/40">{p.pay_period_start} – {p.pay_period_end} · maksupäivä {p.pay_date}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge className={`${sb.cls} border text-[10px]`}>{sb.label}</Badge>
                          <p className="text-sm font-mono text-emerald-300 w-24 text-right">{formatEur(p.net_total)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>
          </div>

          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="legal-banner">
            <AlertTriangle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-100 leading-relaxed">
              <p className="font-semibold mb-1">Lakivaatimukset huomioitu</p>
              <p>Palkkalaskelma sisältää kaikki pakolliset tiedot (osapuolet, ajanjakso, peruspalkka, lisät, luontoisedut, ennakonpidätys, TyEL, työttömyysvakuutus, sairausvakuutus, brutto- ja nettopalkka). Vuosikertymät päivittyvät automaattisesti. Maksetut laskelmat säilytetään <strong>vähintään 6 vuotta</strong> tilikauden päättymisestä.</p>
            </div>
          </div>
        </TabsContent>

        {/* EMPLOYEES */}
        <TabsContent value="employees">
          <Panel title={`Työntekijät (${filteredEmployees.length})`}>
            {filteredEmployees.length === 0 ? (
              <EmptyState icon={<Users className="w-8 h-8" />} message="Ei työntekijöitä" action={<Button onClick={openCreateEmp} className="btn-luxe">Lisää ensimmäinen</Button>} />
            ) : (
              <div className="overflow-x-auto" data-testid="employees-table">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-white/40">
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-2">Työntekijä</th>
                      <th className="text-left py-2 px-2">Tyyppi</th>
                      <th className="text-left py-2 px-2">HETU</th>
                      <th className="text-right py-2 px-2">Kk-palkka / Tuntihinta</th>
                      <th className="text-right py-2 px-2">Vero %</th>
                      <th className="text-right py-2 px-2">Toiminnot</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredEmployees.map(e => (
                      <tr key={e.id} className="hover:bg-white/[0.02]" data-testid={`employee-row-${e.id}`}>
                        <td className="py-2 px-2">
                          <p className="text-white">{e.first_name} {e.last_name}</p>
                          <p className="text-[10px] text-white/40">{e.email || "—"}</p>
                        </td>
                        <td className="py-2 px-2 text-white/70 text-xs">{EMPLOYMENT_TYPES.find(t => t.value === e.employment_type)?.label || e.employment_type}</td>
                        <td className="py-2 px-2 font-mono text-xs text-white/60">{e.national_id || "—"}</td>
                        <td className="py-2 px-2 text-right text-white/80">
                          {e.employment_type === "hourly" ? `${formatEur(e.hourly_rate)} / h` : formatEur(e.monthly_salary)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-xs text-amber-200">
                          {(e.tax_card?.base_percent || 0).toFixed(1)}% / {(e.tax_card?.additional_percent || 0).toFixed(1)}%
                        </td>
                        <td className="py-2 px-2 text-right space-x-1">
                          <Link to={`/hr/payslip/new?employee=${e.id}`}>
                            <Button size="sm" variant="ghost" className="text-amber-300 hover:bg-amber-500/10">
                              <FilePlus2 className="w-3.5 h-3.5" />
                            </Button>
                          </Link>
                          <Button size="sm" variant="ghost" onClick={() => openPortalAccess(e)} className={`hover:bg-emerald-500/10 ${e.portal_enabled ? "text-emerald-300" : "text-white/60"}`} title={e.portal_enabled ? `Portaali käytössä (${e.portal_email})` : "Aseta portaalipääsy"} data-testid={`emp-portal-${e.id}`}>
                            <KeyRound className="w-3.5 h-3.5" />
                          </Button>
                          {e.portal_enabled && (
                            <Button size="sm" variant="ghost" onClick={() => disablePortalAccess(e)} className="text-amber-300 hover:bg-amber-500/10" title="Poista portaalipääsy">
                              <Users className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => openEditEmp(e)} data-testid={`emp-edit-${e.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteEmployee(e)} className="text-red-300 hover:bg-red-500/10" data-testid={`emp-delete-${e.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </TabsContent>

        {/* PAYSLIPS */}
        <TabsContent value="payslips">
          <div className="flex gap-2 mb-3 flex-wrap">
            {["", "draft", "paid", "sent", "cancelled"].map(s => (
              <button
                key={s || "all"}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs uppercase tracking-wider transition ${statusFilter === s ? "bg-white/10 text-white" : "text-white/50 hover:text-white"}`}
                data-testid={`filter-${s || "all"}`}
              >
                {s ? STATUS_BADGE[s]?.label : "Kaikki"}
              </button>
            ))}
          </div>
          <Panel title={`Palkkalaskelmat (${filteredPayslips.length})`}>
            {filteredPayslips.length === 0 ? (
              <EmptyState icon={<Receipt className="w-8 h-8" />} message="Ei palkkalaskelmia – luo uusi" action={<Link to="/hr/payslip/new"><Button className="btn-luxe">Uusi palkkalaskelma</Button></Link>} />
            ) : (
              <div className="overflow-x-auto" data-testid="payslips-table">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-white/40">
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-2">Nro</th>
                      <th className="text-left py-2 px-2">Työntekijä</th>
                      <th className="text-left py-2 px-2">Kausi</th>
                      <th className="text-right py-2 px-2">Brutto</th>
                      <th className="text-right py-2 px-2">Vähennykset</th>
                      <th className="text-right py-2 px-2">Netto</th>
                      <th className="text-center py-2 px-2">Tila</th>
                      <th className="text-right py-2 px-2">Toiminnot</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredPayslips.map(p => {
                      const sb = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
                      return (
                        <tr key={p.id} className="hover:bg-white/[0.02]" data-testid={`payslip-row-${p.id}`}>
                          <td className="py-2 px-2 font-mono text-xs text-white/60">{p.payslip_number}</td>
                          <td className="py-2 px-2 text-white">{p.employee_name}</td>
                          <td className="py-2 px-2 text-white/60 text-xs">{p.pay_period_start} – {p.pay_period_end}</td>
                          <td className="py-2 px-2 text-right font-mono text-white/80">{formatEur(p.gross_total)}</td>
                          <td className="py-2 px-2 text-right font-mono text-red-300">-{formatEur(p.deductions_total)}</td>
                          <td className="py-2 px-2 text-right font-mono text-emerald-300 font-semibold">{formatEur(p.net_total)}</td>
                          <td className="py-2 px-2 text-center"><Badge className={`${sb.cls} border text-[10px]`}>{sb.label}</Badge></td>
                          <td className="py-2 px-2 text-right space-x-1 whitespace-nowrap">
                            <Button size="sm" variant="ghost" onClick={() => downloadPdf(p)} data-testid={`payslip-pdf-${p.id}`} title="Lataa PDF">
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => downloadTulorekisteriXml(p)} title="Tulorekisteri-XML" data-testid={`payslip-xml-${p.id}`} className="text-blue-300 hover:bg-blue-500/10">
                              <FileCode2 className="w-3.5 h-3.5" />
                            </Button>
                            {p.status === "draft" && (
                              <>
                                <Link to={`/hr/payslip/${p.id}`}>
                                  <Button size="sm" variant="ghost" data-testid={`payslip-edit-${p.id}`}>
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                </Link>
                                <Button size="sm" variant="ghost" onClick={() => markPaid(p.id)} className="text-emerald-300 hover:bg-emerald-500/10" title="Merkitse maksetuksi" data-testid={`payslip-mark-paid-${p.id}`}>
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => deletePayslip(p)} className="text-red-300 hover:bg-red-500/10" data-testid={`payslip-delete-${p.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </TabsContent>

        {/* TIME ENTRIES */}
        <TabsContent value="time-entries">
          <Panel title="Työajanseuranta">
            <CrudList config={timeEntriesConfig} />
          </Panel>
        </TabsContent>

        {/* RUNS (Palkkalistat) */}
        <TabsContent value="runs">
          <Panel
            title={`Palkkalistat (${runs.length})`}
            action={
              <Button size="sm" onClick={openRunDialog} className="btn-luxe inline-flex items-center gap-1" data-testid="run-new">
                <PlayCircle className="w-3.5 h-3.5" /> Aja uusi palkkalista
              </Button>
            }
          >
            <p className="text-[11px] text-white/50 mb-3">
              Palkkalistalla voit luoda kuukauden palkkalaskelmat KAIKILLE valitsemillesi työntekijöille kerralla.
              Kuukausipalkkaisten peruspalkka esitäytetään automaattisesti. Voit muokata yksittäisiä laskelmia ennen maksamista.
            </p>
            {runs.length === 0 ? (
              <EmptyState icon={<ListChecks className="w-8 h-8" />} message="Ei vielä palkkalistoja" action={<Button onClick={openRunDialog} className="btn-luxe">Aja ensimmäinen</Button>} />
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-white/40">
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-2">Nimi</th>
                    <th className="text-left py-2 px-2">Kausi</th>
                    <th className="text-left py-2 px-2">Maksupäivä</th>
                    <th className="text-right py-2 px-2">Työntekijöitä</th>
                    <th className="text-right py-2 px-2">Brutto yht.</th>
                    <th className="text-right py-2 px-2">Netto yht.</th>
                    <th className="text-center py-2 px-2">Tila</th>
                    <th className="text-right py-2 px-2">Toiminnot</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {runs.map(r => {
                    const sb = STATUS_BADGE[r.status] || STATUS_BADGE.draft;
                    return (
                      <tr key={r.id} className="hover:bg-white/[0.02]" data-testid={`run-row-${r.id}`}>
                        <td className="py-2 px-2 text-white">{r.name}</td>
                        <td className="py-2 px-2 text-white/60 text-xs">{r.pay_period_start} – {r.pay_period_end}</td>
                        <td className="py-2 px-2 text-white/60 text-xs">{r.pay_date}</td>
                        <td className="py-2 px-2 text-right text-white/80">{r.employee_count}</td>
                        <td className="py-2 px-2 text-right font-mono">{formatEur(r.totals?.gross || 0)}</td>
                        <td className="py-2 px-2 text-right font-mono text-emerald-300">{formatEur(r.totals?.net || 0)}</td>
                        <td className="py-2 px-2 text-center"><Badge className={`${sb.cls} border text-[10px]`}>{sb.label}</Badge></td>
                        <td className="py-2 px-2 text-right space-x-1 whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => openRunDetail(r)} data-testid={`run-view-${r.id}`} title="Näytä laskelmat">
                            <Search className="w-3.5 h-3.5" />
                          </Button>
                          {r.status === "draft" && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => markRunPaid(r)} className="text-emerald-300 hover:bg-emerald-500/10" title="Maksa koko lista" data-testid={`run-pay-${r.id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteRun(r)} className="text-red-300 hover:bg-red-500/10" data-testid={`run-delete-${r.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Panel>
        </TabsContent>

        {/* VACATIONS */}
        <TabsContent value="vacations">
          <Panel
            title={`Lomakertymät (${vacations.length})`}
            action={
              <Button size="sm" onClick={() => setVacDialog({ open: true, mode: "create", data: { ...defaultVacation, vacation_year: new Date().getFullYear() } })} className="btn-luxe inline-flex items-center gap-1" data-testid="vac-new">
                <Plus className="w-3.5 h-3.5" /> Uusi
              </Button>
            }
          >
            {vacations.length === 0 ? (
              <EmptyState icon={<Briefcase className="w-8 h-8" />} message="Ei lomakertymiä – lisää ensimmäinen" />
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-white/40">
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-2">Vuosi</th>
                    <th className="text-left py-2 px-2">Työntekijä</th>
                    <th className="text-right py-2 px-2">Kertynyt pv</th>
                    <th className="text-right py-2 px-2">Pidetty pv</th>
                    <th className="text-right py-2 px-2">Lomaraha kertynyt</th>
                    <th className="text-right py-2 px-2">Lomaraha maksettu</th>
                    <th className="text-right py-2 px-2">Toiminnot</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {vacations.map(v => (
                    <tr key={v.id} className="hover:bg-white/[0.02]">
                      <td className="py-2 px-2 text-white">{v.vacation_year}</td>
                      <td className="py-2 px-2 text-white/80">{empName(v.employee_id)}</td>
                      <td className="py-2 px-2 text-right font-mono">{v.accrued_days}</td>
                      <td className="py-2 px-2 text-right font-mono text-amber-200">{v.used_days}</td>
                      <td className="py-2 px-2 text-right font-mono">{formatEur(v.holiday_pay_accrued)}</td>
                      <td className="py-2 px-2 text-right font-mono text-emerald-300">{formatEur(v.holiday_pay_paid)}</td>
                      <td className="py-2 px-2 text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setVacDialog({ open: true, mode: "edit", data: v })}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteRow("/payroll/vacations", v.id, "lomakertymä")} className="text-red-300"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </TabsContent>

        {/* ABSENCES */}
        <TabsContent value="absences">
          <Panel
            title={`Poissaolot (${absences.length})`}
            action={
              <Button size="sm" onClick={() => setAbsDialog({ open: true, mode: "create", data: defaultAbsence })} className="btn-luxe inline-flex items-center gap-1" data-testid="abs-new">
                <Plus className="w-3.5 h-3.5" /> Uusi
              </Button>
            }
          >
            {absences.length === 0 ? (
              <EmptyState icon={<Clock className="w-8 h-8" />} message="Ei poissaoloja – lisää ensimmäinen" />
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-white/40">
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-2">Tyyppi</th>
                    <th className="text-left py-2 px-2">Työntekijä</th>
                    <th className="text-left py-2 px-2">Alkaa</th>
                    <th className="text-left py-2 px-2">Päättyy</th>
                    <th className="text-right py-2 px-2">Päiviä</th>
                    <th className="text-center py-2 px-2">Palkallinen</th>
                    <th className="text-center py-2 px-2">Vaikuttaa lomaan</th>
                    <th className="text-right py-2 px-2">Toiminnot</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {absences.map(a => (
                    <tr key={a.id} className="hover:bg-white/[0.02]">
                      <td className="py-2 px-2 text-white">{absenceLabel(a.absence_type)}</td>
                      <td className="py-2 px-2 text-white/80">{empName(a.employee_id)}</td>
                      <td className="py-2 px-2 text-white/60">{a.start_date}</td>
                      <td className="py-2 px-2 text-white/60">{a.end_date}</td>
                      <td className="py-2 px-2 text-right font-mono">{a.days}</td>
                      <td className="py-2 px-2 text-center">{a.paid ? "✓" : "—"}</td>
                      <td className="py-2 px-2 text-center">{a.affects_vacation_accrual ? "✓" : "—"}</td>
                      <td className="py-2 px-2 text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setAbsDialog({ open: true, mode: "edit", data: a })}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteRow("/payroll/absences", a.id, "poissaolo")} className="text-red-300"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </TabsContent>

        {/* REPORTS */}
        <TabsContent value="reports">
          <Panel
            title="Palkkaraportit"
            action={
              <div className="flex items-center gap-2">
                <label className="text-xs text-white/60">Vuosi:</label>
                <Input
                  type="number"
                  value={reportYear}
                  onChange={e => setReportYear(parseInt(e.target.value) || new Date().getFullYear())}
                  className="w-24 h-8 bg-black/30 border-white/10"
                  data-testid="report-year"
                />
              </div>
            }
          >
            <Tabs value={reportTab} onValueChange={setReportTab}>
              <TabsList className="bg-white/[0.04] mb-3">
                <TabsTrigger value="bookkeeping" data-testid="report-tab-bookkeeping">Kirjanpidon raportti</TabsTrigger>
                <TabsTrigger value="summary" data-testid="report-tab-summary">Palkkojen yhteenveto</TabsTrigger>
                <TabsTrigger value="vacation" data-testid="report-tab-vacation">Lomalaskennan yhteenveto</TabsTrigger>
              </TabsList>

              {/* Kirjanpidon raportti */}
              <TabsContent value="bookkeeping">
                {!bkReport ? <p className="text-xs text-white/40 italic">Ei dataa</p> : bkReport.months.length === 0 ? (
                  <p className="text-xs text-white/40 italic">Ei palkkalaskelmia vuonna {reportYear}</p>
                ) : (
                  <div className="overflow-x-auto" data-testid="report-bookkeeping-table">
                    <table className="w-full text-sm">
                      <thead className="text-[10px] uppercase tracking-widest text-white/40">
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 px-2">Kuukausi</th>
                          <th className="text-right py-2 px-2">Lkm</th>
                          <th className="text-right py-2 px-2">Brutto</th>
                          <th className="text-right py-2 px-2">Netto</th>
                          <th className="text-right py-2 px-2">Ennakonpidätys</th>
                          <th className="text-right py-2 px-2">TyEL</th>
                          <th className="text-right py-2 px-2">TVR</th>
                          <th className="text-right py-2 px-2">SaVa</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {bkReport.months.map(m => (
                          <tr key={m.month} className="hover:bg-white/[0.02]">
                            <td className="py-2 px-2 text-white">{m.month}</td>
                            <td className="py-2 px-2 text-right">{m.count}</td>
                            <td className="py-2 px-2 text-right font-mono text-blue-300">{formatEur(m.gross)}</td>
                            <td className="py-2 px-2 text-right font-mono text-emerald-300">{formatEur(m.net)}</td>
                            <td className="py-2 px-2 text-right font-mono text-red-300">{formatEur(m.tax)}</td>
                            <td className="py-2 px-2 text-right font-mono">{formatEur(m.tyel)}</td>
                            <td className="py-2 px-2 text-right font-mono">{formatEur(m.unemployment)}</td>
                            <td className="py-2 px-2 text-right font-mono">{formatEur(m.health_insurance)}</td>
                          </tr>
                        ))}
                        <tr className="bg-amber-500/5 font-semibold border-t-2 border-amber-500/20">
                          <td className="py-2 px-2 text-amber-200">YHTEENSÄ</td>
                          <td className="py-2 px-2 text-right">{bkReport.totals.count}</td>
                          <td className="py-2 px-2 text-right font-mono text-blue-300">{formatEur(bkReport.totals.gross)}</td>
                          <td className="py-2 px-2 text-right font-mono text-emerald-300">{formatEur(bkReport.totals.net)}</td>
                          <td className="py-2 px-2 text-right font-mono text-red-300">{formatEur(bkReport.totals.tax)}</td>
                          <td className="py-2 px-2 text-right font-mono">{formatEur(bkReport.totals.tyel)}</td>
                          <td className="py-2 px-2 text-right font-mono">{formatEur(bkReport.totals.unemployment)}</td>
                          <td className="py-2 px-2 text-right font-mono">{formatEur(bkReport.totals.health_insurance)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>

              {/* Palkkojen yhteenveto */}
              <TabsContent value="summary">
                {!sumReport ? <p className="text-xs text-white/40 italic">Ei dataa</p> : sumReport.employees.length === 0 ? (
                  <p className="text-xs text-white/40 italic">Ei maksettuja laskelmia vuonna {reportYear}</p>
                ) : (
                  <div className="overflow-x-auto" data-testid="report-summary-table">
                    <table className="w-full text-sm">
                      <thead className="text-[10px] uppercase tracking-widest text-white/40">
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 px-2">Työntekijä</th>
                          <th className="text-right py-2 px-2">Laskelmia</th>
                          <th className="text-right py-2 px-2">Brutto</th>
                          <th className="text-right py-2 px-2">Netto</th>
                          <th className="text-right py-2 px-2">Vero</th>
                          <th className="text-right py-2 px-2">TyEL</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {sumReport.employees.map(e => (
                          <tr key={e.employee_id} className="hover:bg-white/[0.02]">
                            <td className="py-2 px-2 text-white">{e.employee_name}</td>
                            <td className="py-2 px-2 text-right">{e.payslips_count}</td>
                            <td className="py-2 px-2 text-right font-mono text-blue-300">{formatEur(e.gross)}</td>
                            <td className="py-2 px-2 text-right font-mono text-emerald-300">{formatEur(e.net)}</td>
                            <td className="py-2 px-2 text-right font-mono text-red-300">{formatEur(e.tax)}</td>
                            <td className="py-2 px-2 text-right font-mono">{formatEur(e.tyel)}</td>
                          </tr>
                        ))}
                        <tr className="bg-amber-500/5 font-semibold border-t-2 border-amber-500/20">
                          <td className="py-2 px-2 text-amber-200">YHTEENSÄ</td>
                          <td className="py-2 px-2 text-right">—</td>
                          <td className="py-2 px-2 text-right font-mono text-blue-300">{formatEur(sumReport.totals.gross)}</td>
                          <td className="py-2 px-2 text-right font-mono text-emerald-300">{formatEur(sumReport.totals.net)}</td>
                          <td className="py-2 px-2 text-right font-mono text-red-300">{formatEur(sumReport.totals.tax)}</td>
                          <td className="py-2 px-2 text-right font-mono">{formatEur(sumReport.totals.tyel)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>

              {/* Lomalaskennan yhteenveto */}
              <TabsContent value="vacation">
                {!vacReport ? <p className="text-xs text-white/40 italic">Ei dataa</p> : vacReport.rows.length === 0 ? (
                  <p className="text-xs text-white/40 italic">Ei lomakertymiä vuonna {reportYear}</p>
                ) : (
                  <div className="overflow-x-auto" data-testid="report-vacation-table">
                    <table className="w-full text-sm">
                      <thead className="text-[10px] uppercase tracking-widest text-white/40">
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 px-2">Työntekijä</th>
                          <th className="text-right py-2 px-2">Kertynyt pv</th>
                          <th className="text-right py-2 px-2">Pidetty pv</th>
                          <th className="text-right py-2 px-2">Jäljellä pv</th>
                          <th className="text-right py-2 px-2">Lomaraha kertynyt</th>
                          <th className="text-right py-2 px-2">Lomaraha maksettu</th>
                          <th className="text-right py-2 px-2">Jäljellä</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {vacReport.rows.map(v => (
                          <tr key={v.id} className="hover:bg-white/[0.02]">
                            <td className="py-2 px-2 text-white">{v.employee_name}</td>
                            <td className="py-2 px-2 text-right font-mono">{v.accrued_days}</td>
                            <td className="py-2 px-2 text-right font-mono text-amber-200">{v.used_days}</td>
                            <td className="py-2 px-2 text-right font-mono text-emerald-300">{v.remaining_days}</td>
                            <td className="py-2 px-2 text-right font-mono">{formatEur(v.holiday_pay_accrued)}</td>
                            <td className="py-2 px-2 text-right font-mono">{formatEur(v.holiday_pay_paid)}</td>
                            <td className="py-2 px-2 text-right font-mono text-emerald-300">{formatEur(v.holiday_pay_remaining)}</td>
                          </tr>
                        ))}
                        <tr className="bg-amber-500/5 font-semibold border-t-2 border-amber-500/20">
                          <td className="py-2 px-2 text-amber-200">YHTEENSÄ</td>
                          <td className="py-2 px-2 text-right font-mono">{vacReport.totals.accrued}</td>
                          <td className="py-2 px-2 text-right font-mono">{vacReport.totals.used}</td>
                          <td className="py-2 px-2 text-right font-mono">{vacReport.totals.remaining}</td>
                          <td className="py-2 px-2 text-right font-mono">{formatEur(vacReport.totals.pay_accrued)}</td>
                          <td className="py-2 px-2 text-right font-mono">{formatEur(vacReport.totals.pay_paid)}</td>
                          <td className="py-2 px-2 text-right font-mono">—</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </Panel>
        </TabsContent>

        {/* ARCHIVE */}
        <TabsContent value="archive">
          <Panel title="Vuosikertymät ja arkisto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <KpiCard icon={<Wallet className="w-4 h-4" />} label="Vuositulot brutto (YTD)" value={formatEur(stats?.ytd_gross || 0)} accent="text-emerald-300" />
              <KpiCard icon={<Receipt className="w-4 h-4" />} label="Ennakonpidätys YTD" value={formatEur(stats?.ytd_tax || 0)} accent="text-red-300" />
              <KpiCard icon={<Briefcase className="w-4 h-4" />} label="TyEL YTD" value={formatEur(stats?.ytd_tyel || 0)} accent="text-blue-300" />
            </div>
            <div className="space-y-3">
              <p className="text-sm text-white/70">Maksetut palkkalaskelmat säilytetään kirjanpitomateriaalina vähintään 6 vuotta tilikauden päättymisestä. Voit ladata kunkin laskelman PDF:nä.</p>
              {payslips.filter(p => p.status === "paid").length === 0 ? (
                <p className="text-xs text-white/40 italic">Ei vielä maksettuja laskelmia.</p>
              ) : (
                <ul className="space-y-1 text-xs">
                  {payslips.filter(p => p.status === "paid").map(p => (
                    <li key={p.id} className="flex items-center justify-between py-2 border-b border-white/5">
                      <span className="text-white/80">
                        <span className="font-mono text-white/40">{p.payslip_number}</span> · {p.employee_name} · {p.pay_date}
                      </span>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(p)} className="inline-flex items-center gap-1 text-amber-300 hover:bg-amber-500/10">
                        <Download className="w-3 h-3" /> PDF
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Panel>
        </TabsContent>

        {/* === Henkilökunta-välilehti (Phase 17B) === */}
        <TabsContent value="staff">
          <ModuleStaffTab moduleKey="hr" moduleLabel="HR & Palkat" />
        </TabsContent>
      </Tabs>

      {/* Employee Dialog */}
      <Dialog open={empDialog.open} onOpenChange={v => !v && setEmpDialog({ open: false, mode: "create", data: defaultEmployee })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle>{empDialog.mode === "create" ? "Uusi työntekijä" : "Muokkaa työntekijää"}</DialogTitle>
          </DialogHeader>
          <EmployeeForm data={empDialog.data} setData={d => setEmpDialog(prev => ({ ...prev, data: d }))} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmpDialog({ open: false, mode: "create", data: defaultEmployee })}>Peruuta</Button>
            <Button onClick={saveEmployee} disabled={saving} className="btn-luxe" data-testid="employee-save">{saving ? "Tallennetaan…" : "Tallenna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vacation Dialog */}
      <Dialog open={vacDialog.open} onOpenChange={v => !v && setVacDialog({ open: false, mode: "create", data: defaultVacation })}>
        <DialogContent className="max-w-lg" data-testid="vacation-dialog">
          <DialogHeader><DialogTitle>{vacDialog.mode === "create" ? "Uusi lomakertymä" : "Muokkaa lomakertymää"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Työntekijä">
              <select value={vacDialog.data.employee_id} onChange={e => setVacDialog({ ...vacDialog, data: { ...vacDialog.data, employee_id: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="">— valitse —</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
              </select>
            </Field>
            <Field label="Lomavuosi"><Input type="number" value={vacDialog.data.vacation_year} onChange={e => setVacDialog({ ...vacDialog, data: { ...vacDialog.data, vacation_year: parseInt(e.target.value) || new Date().getFullYear() } })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kertyneet päivät"><Input type="number" step="0.5" value={vacDialog.data.accrued_days} onChange={e => setVacDialog({ ...vacDialog, data: { ...vacDialog.data, accrued_days: parseFloat(e.target.value) || 0 } })} /></Field>
              <Field label="Pidetyt päivät"><Input type="number" step="0.5" value={vacDialog.data.used_days} onChange={e => setVacDialog({ ...vacDialog, data: { ...vacDialog.data, used_days: parseFloat(e.target.value) || 0 } })} /></Field>
              <Field label="Lomaraha kertynyt €"><Input type="number" step="0.01" value={vacDialog.data.holiday_pay_accrued} onChange={e => setVacDialog({ ...vacDialog, data: { ...vacDialog.data, holiday_pay_accrued: parseFloat(e.target.value) || 0 } })} /></Field>
              <Field label="Lomaraha maksettu €"><Input type="number" step="0.01" value={vacDialog.data.holiday_pay_paid} onChange={e => setVacDialog({ ...vacDialog, data: { ...vacDialog.data, holiday_pay_paid: parseFloat(e.target.value) || 0 } })} /></Field>
            </div>
            <Field label="Muistiinpanot"><textarea value={vacDialog.data.notes || ""} onChange={e => setVacDialog({ ...vacDialog, data: { ...vacDialog.data, notes: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[60px]" /></Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVacDialog({ open: false, mode: "create", data: defaultVacation })}>Peruuta</Button>
            <Button onClick={saveVacation} disabled={saving} className="btn-luxe" data-testid="vacation-save">{saving ? "Tallennetaan…" : "Tallenna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Absence Dialog */}
      <Dialog open={absDialog.open} onOpenChange={v => !v && setAbsDialog({ open: false, mode: "create", data: defaultAbsence })}>
        <DialogContent className="max-w-lg" data-testid="absence-dialog">
          <DialogHeader><DialogTitle>{absDialog.mode === "create" ? "Uusi poissaolo" : "Muokkaa poissaoloa"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Työntekijä">
              <select value={absDialog.data.employee_id} onChange={e => setAbsDialog({ ...absDialog, data: { ...absDialog.data, employee_id: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="">— valitse —</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
              </select>
            </Field>
            <Field label="Tyyppi">
              <select value={absDialog.data.absence_type} onChange={e => setAbsDialog({ ...absDialog, data: { ...absDialog.data, absence_type: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
                <option value="sick">Sairausloma</option>
                <option value="parental">Vanhempainvapaa</option>
                <option value="unpaid">Palkaton vapaa</option>
                <option value="study">Opintovapaa</option>
                <option value="other">Muu</option>
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Alkaa"><Input type="date" value={absDialog.data.start_date} onChange={e => setAbsDialog({ ...absDialog, data: { ...absDialog.data, start_date: e.target.value } })} /></Field>
              <Field label="Päättyy"><Input type="date" value={absDialog.data.end_date} onChange={e => setAbsDialog({ ...absDialog, data: { ...absDialog.data, end_date: e.target.value } })} /></Field>
              <Field label="Päiviä"><Input type="number" step="0.5" value={absDialog.data.days} onChange={e => setAbsDialog({ ...absDialog, data: { ...absDialog.data, days: parseFloat(e.target.value) || 0 } })} /></Field>
              <div className="flex flex-col gap-2 pt-5">
                <label className="text-xs text-white/70 flex items-center gap-2">
                  <input type="checkbox" checked={!!absDialog.data.paid} onChange={e => setAbsDialog({ ...absDialog, data: { ...absDialog.data, paid: e.target.checked } })} />
                  Palkallinen
                </label>
                <label className="text-xs text-white/70 flex items-center gap-2">
                  <input type="checkbox" checked={!!absDialog.data.affects_vacation_accrual} onChange={e => setAbsDialog({ ...absDialog, data: { ...absDialog.data, affects_vacation_accrual: e.target.checked } })} />
                  Vaikuttaa lomakertymään
                </label>
              </div>
            </div>
            <Field label="Muistiinpanot"><textarea value={absDialog.data.notes || ""} onChange={e => setAbsDialog({ ...absDialog, data: { ...absDialog.data, notes: e.target.value } })} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[60px]" /></Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAbsDialog({ open: false, mode: "create", data: defaultAbsence })}>Peruuta</Button>
            <Button onClick={saveAbsence} disabled={saving} className="btn-luxe" data-testid="absence-save">{saving ? "Tallennetaan…" : "Tallenna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Run dialog */}
      <Dialog open={runDialog.open} onOpenChange={v => !v && setRunDialog({ open: false, data: defaultRun() })}>
        <DialogContent className="max-w-2xl" data-testid="run-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PlayCircle className="w-5 h-5 text-amber-300" /> Aja uusi palkkalista</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Nimi (valinnainen)">
              <Input value={runDialog.data.name} onChange={e => setRunDialog({ ...runDialog, data: { ...runDialog.data, name: e.target.value } })} placeholder="esim. Kesäkuun palkat 2026" data-testid="run-name" />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Kausi alkaa"><Input type="date" value={runDialog.data.pay_period_start} onChange={e => setRunDialog({ ...runDialog, data: { ...runDialog.data, pay_period_start: e.target.value } })} data-testid="run-start" /></Field>
              <Field label="Kausi päättyy"><Input type="date" value={runDialog.data.pay_period_end} onChange={e => setRunDialog({ ...runDialog, data: { ...runDialog.data, pay_period_end: e.target.value } })} data-testid="run-end" /></Field>
              <Field label="Maksupäivä"><Input type="date" value={runDialog.data.pay_date} onChange={e => setRunDialog({ ...runDialog, data: { ...runDialog.data, pay_date: e.target.value } })} data-testid="run-paydate" /></Field>
            </div>
            <div>
              <Label className="text-xs text-white/60">Työntekijät (tyhjä valinta = kaikki aktiiviset)</Label>
              <div className="mt-1 border border-white/10 rounded-lg p-2 max-h-56 overflow-y-auto space-y-1 bg-black/30" data-testid="run-employees">
                {employees.filter(e => e.is_active !== false).map(e => {
                  const checked = runDialog.data.employee_ids.includes(e.id);
                  return (
                    <label key={e.id} className="flex items-center gap-2 text-sm py-1 hover:bg-white/[0.03] rounded px-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={ev => {
                          const ids = ev.target.checked
                            ? [...runDialog.data.employee_ids, e.id]
                            : runDialog.data.employee_ids.filter(x => x !== e.id);
                          setRunDialog({ ...runDialog, data: { ...runDialog.data, employee_ids: ids } });
                        }}
                      />
                      <span className="text-white/80 flex-1">{e.first_name} {e.last_name}</span>
                      <span className="text-[10px] text-white/40">{EMPLOYMENT_TYPES.find(t => t.value === e.employment_type)?.label}</span>
                      <span className="text-xs font-mono text-amber-200">
                        {e.employment_type === "hourly" ? `${formatEur(e.hourly_rate)} / h` : formatEur(e.monthly_salary)}
                      </span>
                    </label>
                  );
                })}
                {employees.length === 0 && <p className="text-xs text-white/40 italic text-center py-3">Lisää työntekijöitä ensin</p>}
              </div>
              <p className="text-[10px] text-white/50 mt-1">{runDialog.data.employee_ids.length === 0 ? "→ Käytetään kaikkia aktiivisia työntekijöitä" : `Valittu ${runDialog.data.employee_ids.length} työntekijää`}</p>
            </div>
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-100 leading-relaxed">
              Listaa ajamalla luodaan <strong>luonnokset</strong> kaikille valituille. Peruspalkka esitäytetään työntekijän kuukausi-/tuntipalkasta.
              Voit muokata yksittäisiä laskelmia ennen kuin merkitset koko listan maksetuksi.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRunDialog({ open: false, data: defaultRun() })}>Peruuta</Button>
            <Button onClick={submitRun} disabled={saving} className="btn-luxe" data-testid="run-create">{saving ? "Luodaan…" : "Aja lista"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run detail dialog */}
      <Dialog open={!!runDetail} onOpenChange={v => !v && setRunDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="run-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              {runDetail?.run?.name} <span className="text-xs text-white/40 font-normal">· {runDetail?.run?.pay_period_start} – {runDetail?.run?.pay_period_end}</span>
            </DialogTitle>
          </DialogHeader>
          {runDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3 text-sm">
                <div className="rounded-lg bg-white/[0.03] p-3 border border-white/10">
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Työntekijöitä</p>
                  <p className="text-2xl font-display text-white">{runDetail.run.employee_count}</p>
                </div>
                <div className="rounded-lg bg-white/[0.03] p-3 border border-white/10">
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Brutto</p>
                  <p className="text-2xl font-display text-blue-300">{formatEur(runDetail.run.totals?.gross || 0)}</p>
                </div>
                <div className="rounded-lg bg-white/[0.03] p-3 border border-white/10">
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Netto</p>
                  <p className="text-2xl font-display text-emerald-300">{formatEur(runDetail.run.totals?.net || 0)}</p>
                </div>
                <div className="rounded-lg bg-white/[0.03] p-3 border border-white/10">
                  <p className="text-[10px] uppercase tracking-widest text-white/40">Tila</p>
                  <p className="text-sm">
                    <Badge className={`${(STATUS_BADGE[runDetail.run.status] || STATUS_BADGE.draft).cls} border`}>
                      {(STATUS_BADGE[runDetail.run.status] || STATUS_BADGE.draft).label}
                    </Badge>
                  </p>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-white/40">
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-2">Nro</th>
                    <th className="text-left py-2 px-2">Työntekijä</th>
                    <th className="text-right py-2 px-2">Brutto</th>
                    <th className="text-right py-2 px-2">Vähennykset</th>
                    <th className="text-right py-2 px-2">Netto</th>
                    <th className="text-center py-2 px-2">Tila</th>
                    <th className="text-right py-2 px-2">PDF</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {(runDetail.payslips || []).map(p => {
                    const sb = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
                    return (
                      <tr key={p.id}>
                        <td className="py-2 px-2 font-mono text-xs text-white/60">{p.payslip_number}</td>
                        <td className="py-2 px-2 text-white">{p.employee_name}</td>
                        <td className="py-2 px-2 text-right font-mono">{formatEur(p.gross_total)}</td>
                        <td className="py-2 px-2 text-right font-mono text-red-300">-{formatEur(p.deductions_total)}</td>
                        <td className="py-2 px-2 text-right font-mono text-emerald-300 font-semibold">{formatEur(p.net_total)}</td>
                        <td className="py-2 px-2 text-center"><Badge className={`${sb.cls} border text-[10px]`}>{sb.label}</Badge></td>
                        <td className="py-2 px-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => downloadPdf(p)}><Download className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => downloadTulorekisteriXml(p)} className="text-blue-300"><FileCode2 className="w-3.5 h-3.5" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {runDetail.run.status === "draft" && (
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setRunDetail(null)}>Sulje</Button>
                  <Button onClick={() => markRunPaid(runDetail.run)} className="btn-luxe inline-flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Maksa kaikki ({runDetail.payslips.length})
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Portal access dialog */}
      <Dialog open={portalDialog.open} onOpenChange={v => !v && setPortalDialog({ open: false, employee: null, email: "", password: "" })}>
        <DialogContent className="max-w-md" data-testid="portal-access-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-emerald-300" />
              Aseta portaalipääsy
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-100 leading-relaxed">
              Työntekijä <strong>{portalDialog.employee?.first_name} {portalDialog.employee?.last_name}</strong> saa pääsyn osoitteeseen
              <code className="text-amber-200 ml-1 bg-black/30 px-1.5 py-0.5 rounded">/employee-portal/login</code>.
              Hän voi katsoa <strong>omat työvuoronsa ja palkkalaskelmansa</strong> – ei pääsyä admin-toimintoihin.
            </div>
            <Field label="Sähköposti (kirjautumistunnus)">
              <Input
                type="email"
                value={portalDialog.email}
                onChange={e => setPortalDialog({ ...portalDialog, email: e.target.value })}
                placeholder="etunimi.sukunimi@example.com"
                data-testid="portal-email"
                autoFocus
              />
            </Field>
            <Field label="Tilapäinen salasana">
              <div className="flex gap-2">
                <Input
                  value={portalDialog.password}
                  onChange={e => setPortalDialog({ ...portalDialog, password: e.target.value })}
                  className="font-mono"
                  data-testid="portal-password"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPortalDialog({ ...portalDialog, password: Math.random().toString(36).slice(-10) + "A1!" })}
                  className="border-amber-400/30 whitespace-nowrap"
                  type="button"
                >
                  Luo uusi
                </Button>
              </div>
            </Field>
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2 text-[11px] text-amber-100">
              <p className="font-semibold mb-0.5">⚠ Lähetä työntekijälle:</p>
              <p>1. Linkki: <code className="text-white">/employee-portal/login</code></p>
              <p>2. Sähköposti + tilapäinen salasana yllä</p>
              <p>3. Hän pakotetaan vaihtamaan salasana ensimmäisellä kirjautumisella</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPortalDialog({ open: false, employee: null, email: "", password: "" })}>Peruuta</Button>
            <Button onClick={savePortalAccess} disabled={saving} className="btn-luxe" data-testid="portal-save">
              {saving ? "Tallennetaan…" : "Aseta pääsy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function absenceLabel(t) {
  return { sick: "Sairausloma", parental: "Vanhempainvapaa", unpaid: "Palkaton vapaa", study: "Opintovapaa", other: "Muu" }[t] || t;
}

function EmployeeForm({ data, setData }) {
  const upd = (k, v) => setData({ ...data, [k]: v });
  const updTax = (k, v) => setData({ ...data, tax_card: { ...data.tax_card, [k]: v } });
  const updBen = (k, v) => setData({ ...data, benefits: { ...data.benefits, [k]: v } });

  return (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Etunimi *"><Input value={data.first_name} onChange={e => upd("first_name", e.target.value)} data-testid="emp-first-name" /></Field>
        <Field label="Sukunimi *"><Input value={data.last_name} onChange={e => upd("last_name", e.target.value)} data-testid="emp-last-name" /></Field>
        <Field label="Henkilötunnus (HETU)"><Input value={data.national_id} onChange={e => upd("national_id", e.target.value)} placeholder="121280-123A" data-testid="emp-national-id" /></Field>
        <Field label="Syntymäaika (auto TyEL-ikä)"><Input type="date" value={data.date_of_birth} onChange={e => upd("date_of_birth", e.target.value)} /></Field>
        <Field label="Sähköposti"><Input type="email" value={data.email} onChange={e => upd("email", e.target.value)} data-testid="emp-email" /></Field>
        <Field label="Puhelin"><Input value={data.phone} onChange={e => upd("phone", e.target.value)} /></Field>
        <Field label="Osoite"><Input value={data.address} onChange={e => upd("address", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Postinro"><Input value={data.postal_code} onChange={e => upd("postal_code", e.target.value)} /></Field>
          <Field label="Kaupunki"><Input value={data.city} onChange={e => upd("city", e.target.value)} /></Field>
        </div>
        <Field label="IBAN-tilinumero"><Input value={data.iban} onChange={e => upd("iban", e.target.value)} placeholder="FI21 1234 5600 0007 85" data-testid="emp-iban" /></Field>
        <Field label="Työntekijän numero"><Input value={data.employee_number} onChange={e => upd("employee_number", e.target.value)} /></Field>
      </div>

      <div className="border-t border-white/10 pt-3">
        <p className="text-xs uppercase tracking-widest text-amber-200/70 mb-2">Työsuhde</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Tyyppi">
            <select value={data.employment_type} onChange={e => upd("employment_type", e.target.value)} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="emp-type">
              {EMPLOYMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="TES (työehtosopimus)"><Input value={data.collective_agreement} onChange={e => upd("collective_agreement", e.target.value)} /></Field>
          <Field label="Aloituspäivä"><Input type="date" value={data.start_date} onChange={e => upd("start_date", e.target.value)} /></Field>
          <Field label="Päättymispäivä"><Input type="date" value={data.end_date} onChange={e => upd("end_date", e.target.value)} /></Field>
          {data.employment_type === "monthly" ? (
            <Field label="Kuukausipalkka (€)"><Input type="number" step="0.01" value={data.monthly_salary} onChange={e => upd("monthly_salary", parseFloat(e.target.value) || 0)} data-testid="emp-monthly" /></Field>
          ) : (
            <Field label="Tuntipalkka (€)"><Input type="number" step="0.01" value={data.hourly_rate} onChange={e => upd("hourly_rate", parseFloat(e.target.value) || 0)} data-testid="emp-hourly" /></Field>
          )}
          <Field label="Säännölliset h / kk"><Input type="number" value={data.regular_hours_per_month} onChange={e => upd("regular_hours_per_month", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="Lomapäiviä / vuosi"><Input type="number" value={data.vacation_days_per_year} onChange={e => upd("vacation_days_per_year", parseFloat(e.target.value) || 0)} /></Field>
        </div>
      </div>

      <div className="border-t border-white/10 pt-3">
        <p className="text-xs uppercase tracking-widest text-amber-200/70 mb-2">Verokortti</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Perus-%"><Input type="number" step="0.1" value={data.tax_card.base_percent} onChange={e => updTax("base_percent", parseFloat(e.target.value) || 0)} data-testid="emp-tax-base" /></Field>
          <Field label="Lisä-%"><Input type="number" step="0.1" value={data.tax_card.additional_percent} onChange={e => updTax("additional_percent", parseFloat(e.target.value) || 0)} data-testid="emp-tax-add" /></Field>
          <Field label="Tuloraja (€)"><Input type="number" step="100" value={data.tax_card.income_threshold} onChange={e => updTax("income_threshold", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="Kortin tyyppi">
            <select value={data.tax_card.card_type || "main"} onChange={e => updTax("card_type", e.target.value)} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
              <option value="main">Pääverokortti</option>
              <option value="secondary">Sivutuloverokortti</option>
            </select>
          </Field>
        </div>
      </div>

      <div className="border-t border-white/10 pt-3">
        <p className="text-xs uppercase tracking-widest text-amber-200/70 mb-2">Vakuutukset</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="TyEL-yhtiö"><Input value={data.tyel_company} onChange={e => upd("tyel_company", e.target.value)} placeholder="Veritas, Ilmarinen…" /></Field>
          <Field label="TyEL-sopimusnumero"><Input value={data.tyel_contract_number} onChange={e => upd("tyel_contract_number", e.target.value)} /></Field>
          <Field label="Ay-jäsenmaksu (%)"><Input type="number" step="0.01" value={data.union_fee_percent} onChange={e => upd("union_fee_percent", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="Ammattiliitto"><Input value={data.union} onChange={e => upd("union", e.target.value)} /></Field>
        </div>
      </div>

      <div className="border-t border-white/10 pt-3">
        <p className="text-xs uppercase tracking-widest text-amber-200/70 mb-2">Luontoisedut (verotusarvo €/kk)</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Field label="Puhelinetu"><Input type="number" step="0.01" value={data.benefits.phone} onChange={e => updBen("phone", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="Lounasetu"><Input type="number" step="0.01" value={data.benefits.lunch} onChange={e => updBen("lunch", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="Autoetu"><Input type="number" step="0.01" value={data.benefits.car} onChange={e => updBen("car", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="Asunto-etu"><Input type="number" step="0.01" value={data.benefits.accommodation} onChange={e => updBen("accommodation", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="Muu etu"><Input type="number" step="0.01" value={data.benefits.other} onChange={e => updBen("other", parseFloat(e.target.value) || 0)} /></Field>
        </div>
      </div>

      <Field label="Muistiinpanot">
        <textarea value={data.notes} onChange={e => upd("notes", e.target.value)} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[60px]" />
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-white/60">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
